/**
 * Arkusze instalacji do teczki (pakiet P6, `zadania/2026-10_plan-rynek-dla-domu-produkcja.md`): schemat instalacji
 * (jednokreskowy, w stylu `a4ELEKTR.pdf` z projektu właściciela) i rozmieszczenie urządzeń w domu (obrys domu
 * z Projektu 3D, falownik z jego położenia w modelu, reszta z odpowiedzi prostego audytu). Wspólny arkusz, ramka,
 * tabelka i legenda jak w rzucie dachu (`rzutDachu.js`); PDF składa `arkusze.js`.
 *
 * WEJŚCIE (umowa z teczką, P5), wszystko poza `sprzet.moduly` może być puste:
 *   {
 *     sprzet: {
 *       moduly:   { liczba, mocW, nazwa },
 *       falownik: { nazwa, mocKw, fazy: 1 | 3 | null, hybrydowy: bool | null } | null,
 *       magazyn:  { nazwa, kwh } | null,
 *       lancuchy: [{ modulow, lancuchow }] | null,   // wejścia MPPT; null: podział dobiera wykonawca
 *     },
 *     miejsca: część `miejsca` teczki (`lib/teczka/miejsca.js`, „nie wiem” = 'nie_wiem') albo null,
 *     rzut: `daneRzutu(projekt)` (obrys domu, połacie, moduły, północ) albo null,
 *     falownikNaRzucie: [X, Y] w układzie rzutu (falownik na ścianie w modelu) albo null,
 *   }
 * `daneInstalacjiZProjektu(projekt, { miejsca })` buduje to wejście z Projektu 3D.
 *
 * Czego nie wiemy, nie zgadujemy (zasada 1 w `05`): element bez danych ma przerywany obrys i znak zapytania,
 * a punkt trafia na listę „do sprawdzenia na miejscu”. Położenie urządzeń jest umowne i mówi to dopisek.
 */
import { ukladArkusza, dobierzSkale, mmNaMetr, FORMAT_DOMYSLNY } from './arkusz'
import { srodekWielokata, rysujPolnoc, ROZMIAR } from './pdf'
import { ponumerujArkusze, narysujArkusze, rysujArkusze } from './arkusze'
import { daneRzutu, obrotRysunku } from '../projekt3d/rzut'
import { polozenieFalownika } from '../projekt3d/falownik'
import { zbudujBryle } from '../projekt3d/bryla'
import { ukladProjektu } from '../projekt3d/analiza'
import { modulProjektu, typElementu } from '../projekt3d/projekt'
import {
  normalizujMiejsca, doSprawdzenia, mocPrzylaczeniowaKw, NIE_WIEM, OPERATORZY, MIEJSCA_LICZNIKA, POMIESZCZENIA,
  KONDYGNACJE, OPCJE_FALOWNIKA, OPCJE_MAGAZYNU, ODLEGLOSCI, DROGI_KABLI, ZASIEG_WIFI,
} from '../teczka/miejsca'

const RAD = Math.PI / 180
const LINIA = 2.9 // mm między wierszami opisów 6,3 pt
const MALY = 6.3

const znane = (v) => Boolean(v) && v !== NIE_WIEM
const opcja = (lista, k) => (znane(k) ? lista.find(([x]) => x === k)?.[1] || null : null)
const liczba = (x, miejsc = 1) => (x == null ? '' : Number(x).toLocaleString('pl-PL', { maximumFractionDigits: miejsc }))
const modulow = (n) => (n === 1 ? 'moduł' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'moduły' : 'modułów')
const malaLitera = (t) => (t ? t.charAt(0).toLowerCase() + t.slice(1) : t)

/** Nazwa modelu z katalogu bez powtórzonego producenta. */
export const nazwaModelu = (m) => {
  if (!m) return ''
  const nazwa = m.nazwa || m.seria || ''
  if (!m.producent) return nazwa
  return nazwa.toLowerCase().includes(m.producent.toLowerCase()) ? nazwa : [m.producent, nazwa].filter(Boolean).join(' ')
}

/**
 * Wejście arkuszy instalacji z Projektu 3D (opis kształtu w nagłówku). `bryla`, `uklad` i `modul` można podać
 * z ekranu; `miejsca` to część teczki z prostego audytu. Łańcuchów Projekt 3D nie liczy, więc `lancuchy: null`.
 */
export const daneInstalacjiZProjektu = (projekt, { bryla: brylaGotowa, uklad: ukladGotowy, modul: modulGotowy, miejsca = null } = {}) => {
  const lok = projekt.lokalizacja || {}
  const zbieznosc = lok.zbieznosc ?? 0
  const modul = modulGotowy || modulProjektu(projekt.moduly)
  const bryla = brylaGotowa || zbudujBryle(projekt.budynek)
  const uklad = ukladGotowy || ukladProjektu({
    bryla, projekt: { ...projekt, elementy: (projekt.elementy || []).filter((e) => typElementu(e.typ)?.naDachu) },
    modul, lat: lok.lat ?? 52, lon: lok.lon ?? 19, zbieznosc,
  })
  const rzut = daneRzutu(projekt, { bryla, uklad, modul })
  const { naRzut } = obrotRysunku({ skrzydla: projekt.budynek?.skrzydla || [], zbieznosc })
  const f = polozenieFalownika({ projekt, bryla, uklad })
  const fm = projekt.falownik?.model
  const mm = projekt.magazyn?.model
  const kwh = mm?.pojemnoscKwh || projekt.magazyn?.pojemnosc || null
  return {
    sprzet: {
      moduly: { liczba: rzut.liczba, mocW: modul.moc, nazwa: rzut.modul.nazwa },
      falownik: fm ? { nazwa: nazwaModelu(fm), mocKw: fm.mocKw ?? null, fazy: fm.fazy ?? null, hybrydowy: fm.hybrydowy ?? null } : null,
      magazyn: kwh ? { nazwa: nazwaModelu(mm), kwh } : null,
      lancuchy: null,
    },
    miejsca,
    rzut,
    falownikNaRzucie: f ? naRzut([f.pozycja[0], f.pozycja[2]]) : null,
  }
}

/* ═══ Symbole ══════════════════════════════════════════════════════════════════════════════════════ */

const prost = (r, cx, cy, w, h, opcje) => r.prostokat(cx - w / 2, cy - h / 2, w, h, opcje)
const pytajnik = (r, cx, cy, rozmiar = 9) => r.tekst('?', cx, cy + rozmiar * 0.13, { rozmiar, wyrownanie: 'srodek' })
const KRESKI = [1.2, 0.8]

/** Moduł jak na schemacie właściciela: prostokąt z trójkątem u góry. */
export const symbolModulu = (r, cx, cy, w = 8, h = 12) => {
  prost(r, cx, cy, w, h, { grubosc: 0.25 })
  r.wielokat([[cx - w / 2, cy - h / 2], [cx, cy - h / 2 + h * 0.36], [cx + w / 2, cy - h / 2]], { grubosc: 0.18, zamknij: false })
}

/** Rozłącznik DC: obudowa, styk dolny i górny, nóż odchylony. */
export const symbolRozlacznika = (r, cx, cy, w = 10, h = 14) => {
  prost(r, cx, cy, w, h, { grubosc: 0.25 })
  r.linia(cx, cy + h / 2, cx, cy + h * 0.15, { grubosc: 0.25 })
  r.linia(cx, cy + h * 0.15, cx - w * 0.32, cy - h * 0.2, { grubosc: 0.25 })
  r.linia(cx, cy - h / 2, cx, cy - h * 0.18, { grubosc: 0.25 })
  r.linia(cx - 1, cy - h * 0.18, cx + 1, cy - h * 0.18, { grubosc: 0.25 })
}

/** Falownik: kwadrat z przekątną, „=” u góry z lewej i „~” na dole z prawej (jak u właściciela). */
export const symbolFalownika = (r, cx, cy, a = 16, { nieznany = false } = {}) => {
  prost(r, cx, cy, a, a, { grubosc: 0.3, kreski: nieznany ? KRESKI : null })
  r.linia(cx - a / 2, cy + a / 2, cx + a / 2, cy - a / 2, { grubosc: 0.18 })
  r.linia(cx - a * 0.38, cy - a * 0.3, cx - a * 0.14, cy - a * 0.3, { grubosc: 0.25 })
  r.linia(cx - a * 0.38, cy - a * 0.22, cx - a * 0.14, cy - a * 0.22, { grubosc: 0.25 })
  const fala = Array.from({ length: 13 }, (_, i) => [cx + a * 0.12 + (a * 0.26 * i) / 12, cy + a * 0.28 - Math.sin((i / 12) * 2 * Math.PI) * a * 0.05])
  r.wielokat(fala, { grubosc: 0.25, zamknij: false })
  if (nieznany) r.tekst('?', cx + a / 2 + 1.2, cy - a / 2 + 2.6, { rozmiar: 8 })
}

/** Magazyn energii: obudowa i symbol ogniwa (dłuższa płytka dodatnia, krótsza ujemna). */
export const symbolMagazynu = (r, cx, cy, w = 16, h = 10) => {
  prost(r, cx, cy, w, h, { grubosc: 0.3 })
  r.linia(cx - 1.2, cy - h * 0.32, cx - 1.2, cy + h * 0.32, { grubosc: 0.25 })
  r.linia(cx + 1.2, cy - h * 0.16, cx + 1.2, cy + h * 0.16, { grubosc: 0.6 })
  r.linia(cx - w * 0.36, cy, cx - 1.2, cy, { grubosc: 0.18 })
  r.linia(cx + 1.2, cy, cx + w * 0.36, cy, { grubosc: 0.18 })
  r.tekst('+', cx - 3.4, cy - 1.6, { rozmiar: 5.5, wyrownanie: 'srodek' })
  r.tekst('−', cx + 3.6, cy - 1.6, { rozmiar: 5.5, wyrownanie: 'srodek' })
}

/** Rozdzielnica główna i licznik: prostokąt z napisem; licznik ze strzałkami w obie strony (dwukierunkowy). */
export const symbolSkrzynki = (r, cx, cy, napis, { w = 16, h = 12, licznik = false } = {}) => {
  prost(r, cx, cy, w, h, { grubosc: 0.3 })
  r.tekst(napis, cx, cy + (licznik ? 0.2 : 1.1), { rozmiar: 7, wyrownanie: 'srodek' })
  if (licznik) {
    const y = cy + h * 0.27
    r.linia(cx - 4, y, cx + 4, y, { grubosc: 0.18 })
    r.wielokat([[cx + 4, y], [cx + 2.8, y - 0.7], [cx + 2.8, y + 0.7]], { grubosc: 0, wypelnienie: r.kolory.czarny })
    r.wielokat([[cx - 4, y], [cx - 2.8, y - 0.7], [cx - 2.8, y + 0.7]], { grubosc: 0, wypelnienie: r.kolory.czarny })
  }
}

/** Sieć operatora: trzy szyny (jak u właściciela, w pionie, bo linia przychodzi z boku). */
export const symbolSieci = (r, cx, cy, h = 16) => {
  for (const dx of [-2, 0, 2]) r.linia(cx + dx, cy - h / 2, cx + dx, cy + h / 2, { grubosc: 0.35 })
}

/** Urządzenie na rozmieszczeniu: prostokąt z kodem; przerywany i z „?”, gdy położenie jest umowne albo nieznane. */
export const symbolUrzadzenia = (r, cx, cy, kod, { umowne = false } = {}) => {
  prost(r, cx, cy, 10, 6.4, { grubosc: 0.35, kreski: umowne ? KRESKI : null, wypelnienie: r.kolory.bialy })
  r.tekst(kod, cx, cy + 1.1, { rozmiar: 6.8, wyrownanie: 'srodek' })
}

const symbolPrzewodu = (kolor, przerywany = false) => (r, cx, cy) => r.linia(cx - 5, cy, cx + 5, cy, { grubosc: 0.45, kolor: r.kolory[kolor], kreski: przerywany ? KRESKI : null })

/* ═══ Wspólne dane z odpowiedzi audytu ═════════════════════════════════════════════════════════════ */

const czescMiejsc = (dane) => (dane.miejsca ? normalizujMiejsca(dane.miejsca) : null)

const fazyInstalacji = (sprzet, m) => {
  if (sprzet.falownik?.fazy === 1 || sprzet.falownik?.fazy === 3) return sprzet.falownik.fazy
  return m && ['1', '3'].includes(m.przylacze.fazy) ? Number(m.przylacze.fazy) : null
}

const miejsceRozdzielnicy = (m) => {
  if (!m) return null
  const czesci = [opcja(POMIESZCZENIA, m.rozdzielnica.pomieszczenie), opcja(KONDYGNACJE, m.rozdzielnica.kondygnacja)].filter(Boolean)
  return czesci.length ? czesci.map(malaLitera).join(', ') : null
}

const miejsceLicznika = (m) => {
  if (!m) return null
  const gdzie = opcja(MIEJSCA_LICZNIKA, m.licznik.gdzie)
  if (!gdzie) return null
  const czesci = m.licznik.gdzie === 'wewnatrz'
    ? [opcja(POMIESZCZENIA, m.licznik.pomieszczenie), opcja(KONDYGNACJE, m.licznik.kondygnacja)].filter(Boolean)
    : []
  return [malaLitera(gdzie), ...czesci.map(malaLitera)].join(', ')
}

/* ═══ Schemat instalacji ═══════════════════════════════════════════════════════════════════════════ */

/** Opis arkusza „Schemat instalacji”: kolumny łańcuchów, elementy toru DC i AC, legenda i dane. */
export const opisSchematuInstalacji = (dane, { format = FORMAT_DOMYSLNY } = {}) => {
  const uklad = ukladArkusza(format)
  const { sprzet } = dane
  const m = czescMiejsc(dane)
  const fazy = fazyInstalacji(sprzet, m)
  const operator = m ? opcja(OPERATORZY, m.przylacze.operator) : null
  const mocPrzylacza = m ? mocPrzylaczeniowaKw(m) : null
  const n = sprzet.moduly.liczba || 0
  const kolumny = sprzet.lancuchy?.length
    ? sprzet.lancuchy.map((w, i) => ({ etykieta: `MPPT ${i + 1}`, modulow: w.modulow, lancuchow: Math.max(1, w.lancuchow || 1), nieznane: false }))
    : [{ etykieta: 'Moduły', modulow: n, lancuchow: 1, nieznane: true }]
  const f = sprzet.falownik
  const g = sprzet.magazyn
  const kwp = (n * (sprzet.moduly.mocW || 0)) / 1000
  const doSprawdzeniaWiersze = [
    !f && 'Model i moc falownika: dobiera wykonawca.',
    !sprzet.lancuchy?.length && 'Podział modułów na łańcuchy: dobiera wykonawca do falownika.',
    !fazy && 'Liczba faz przyłącza: do sprawdzenia.',
    mocPrzylacza == null && 'Moc przyłączeniowa z umowy z operatorem: do sprawdzenia.',
    !operator && 'Operator sieci: do sprawdzenia.',
  ].filter(Boolean)
  return {
    rodzaj: 'schemat',
    tytul: 'Schemat instalacji',
    uklad,
    skala: null,
    kolumny,
    fazy,
    sprzet,
    etykiety: {
      q: ['-Q1 rozłącznik DC', '(osobny albo w falowniku)'],
      u: ['-U1 falownik', ...(f ? [f.nazwa, [f.mocKw ? `${liczba(f.mocKw)} kW` : '', fazy ? `${fazy === 1 ? '1~' : '3~'}` : ''].filter(Boolean).join(', ')].filter(Boolean)
        : ['dobiera wykonawca'])],
      g: g ? ['-G1 magazyn energii', g.nazwa, g.kwh ? `${liczba(g.kwh)} kWh` : ''].filter(Boolean) : null,
      rg: ['-RG rozdzielnica główna', miejsceRozdzielnicy(m) || 'miejsce do sprawdzenia'],
      p: ['-P1 licznik dwukierunkowy', 'operatora', miejsceLicznika(m) || 'miejsce do sprawdzenia'],
      s: [`Sieć ${operator || 'operatora'}`, mocPrzylacza != null ? `moc przyłączeniowa ${liczba(mocPrzylacza)} kW` : 'moc przyłączeniowa do sprawdzenia'],
    },
    napiecieAc: fazy === 1 ? 'AC 1/N/PE 230 V' : fazy === 3 ? 'AC 3/N/PE 400/230 V' : 'AC, liczba faz do sprawdzenia',
    sprzezenieAc: Boolean(g && f && f.hybrydowy === false),
    legenda: [
      { rysuj: (r, cx, cy) => symbolModulu(r, cx, cy, 5, 7.5), opis: `Moduł fotowoltaiczny ${sprzet.moduly.nazwa || ''}`.trim() },
      { rysuj: (r, cx, cy) => symbolRozlacznika(r, cx, cy, 6, 8), opis: 'Rozłącznik DC' },
      { rysuj: (r, cx, cy) => symbolFalownika(r, cx, cy, 7.5), opis: 'Falownik' },
      ...(g ? [{ rysuj: (r, cx, cy) => symbolMagazynu(r, cx, cy, 10, 6), opis: 'Magazyn energii' }] : []),
      { rysuj: (r, cx, cy) => symbolSkrzynki(r, cx, cy, 'RG', { w: 9, h: 6.5 }), opis: 'Rozdzielnica główna budynku' },
      { rysuj: (r, cx, cy) => symbolSkrzynki(r, cx, cy, 'kWh', { w: 9, h: 7, licznik: true }), opis: 'Licznik energii dwukierunkowy' },
      { rysuj: symbolPrzewodu('dc'), opis: 'Przewód DC' },
      { rysuj: symbolPrzewodu('ac'), opis: 'Przewód AC' },
      { rysuj: (r, cx, cy) => { prost(r, cx, cy, 9, 6, { grubosc: 0.25, kreski: KRESKI }); pytajnik(r, cx, cy, 7) }, opis: 'Do ustalenia przez wykonawcę' },
    ],
    opis: {
      naglowek: 'Instalacja',
      wiersze: [
        `${n} ${modulow(n)} × ${liczba(sprzet.moduly.mocW, 0)} W = ${liczba(kwp, 2)} kWp`,
        f ? `Falownik: ${[f.nazwa, f.mocKw ? `${liczba(f.mocKw)} kW` : ''].filter(Boolean).join(', ')}` : null,
        g ? `Magazyn: ${[g.nazwa, g.kwh ? `${liczba(g.kwh)} kWh` : ''].filter(Boolean).join(', ')}` : null,
        ...doSprawdzeniaWiersze,
        'Zabezpieczenia, przekroje przewodów i ograniczniki przepięć dobiera wykonawca (PN-HD 60364-7-712).',
      ].filter(Boolean),
    },
    rysujTresc(r) { rysujSchemat(r, this) },
  }
}

// Napis z kilku wierszy: `kotwica` 'gora' (pierwszy wiersz na y) albo 'dol' (ostatni wiersz kończy się na y).
const napisy = (r, linie, x, y, { kotwica = 'gora', wyrownanie = 'srodek', szer = 36, kolor } = {}) => {
  const wiersze = linie.filter(Boolean).flatMap((l) => r.zawin(l, MALY, szer))
  const y0 = kotwica === 'gora' ? y + 2.2 : y - (wiersze.length - 1) * LINIA
  wiersze.forEach((l, i) => r.tekst(l, x, y0 + i * LINIA, { rozmiar: MALY, wyrownanie, kolor }))
  return wiersze.length
}

const rysujSchemat = (r, a) => {
  const R = a.uklad.rysunek
  const dc = { grubosc: 0.35, kolor: r.kolory.dc }
  const ac = { grubosc: 0.35, kolor: r.kolory.ac }
  // Tor główny w poziomie: rozłącznik, falownik, rozdzielnica, licznik, sieć.
  const yL = R.y + Math.min(R.h * 0.64, 120)
  const x = (u) => R.x + R.w * u
  const Q = x(0.1)
  const U = x(0.32)
  const RG = x(0.57)
  const P = x(0.78)
  const S = x(0.94)

  // ── Łańcuchy (kolumny modułów) ──
  const k = a.kolumny.length
  const podzialka = Math.min(30, (U - 14 - (R.x + 4)) / Math.max(1, k))
  const x0 = k === 1 ? Q : R.x + 4 + podzialka / 2
  const yGora = R.y + 18
  a.kolumny.forEach((kol, i) => {
    const cx = x0 + i * podzialka
    napisy(r, [kol.etykieta, `${kol.lancuchow > 1 ? `${kol.lancuchow} × ` : ''}${kol.modulow} ${modulow(kol.modulow)}`], cx, yGora - 4, { kotwica: 'dol', szer: podzialka - 2 })
    const numery = kol.modulow <= 3 ? Array.from({ length: kol.modulow }, (_, j) => j + 1) : [1, 2, null, kol.modulow]
    let y = yGora + 6
    let poprzedni = null
    for (const nr of numery) {
      if (nr == null) {
        r.linia(cx, y - 3, cx, y + 7, { grubosc: 0.18, kreski: [0.6, 0.8] })
        y += 10
        poprzedni = null
        continue
      }
      if (poprzedni != null) r.linia(cx, y - 3, cx, y - 6 + 0.01, { grubosc: 0.18 })
      symbolModulu(r, cx, y)
      r.tekst(String(nr), cx + 5, y - 3.5, { rozmiar: 6.5 })
      poprzedni = nr
      y += 15
    }
    const dol = y - 9
    if (kol.lancuchow > 1) r.tekst(`×${kol.lancuchow}`, cx - 5.2, dol - 2, { rozmiar: 6.5, wyrownanie: 'prawo' })
    if (kol.nieznane) {
      r.prostokat(cx - 9, yGora - 0.5, 18, dol - yGora + 3, { grubosc: 0.18, kreski: KRESKI })
      napisy(r, ['Podział na łańcuchy dobiera wykonawca'], cx + 11, yGora + 4, { wyrownanie: 'lewo', szer: 34 })
    }
    // Przewód DC do rozłącznika: w dół, w poziomie (każdy łańcuch na swojej wysokości) i w dół do obudowy.
    const yPoz = yL - 20 - i * 2.5
    const wejscie = Q - (k - 1) * 0.9 + i * 1.8
    r.wielokat([[cx, dol], [cx, yPoz], [wejscie, yPoz], [wejscie, yL - 7]], { ...dc, zamknij: false })
  })
  r.tekst('DC', Q + 6.5, yL - 8.5, { rozmiar: MALY, kolor: r.kolory.dc })

  // ── Tor główny ──
  symbolRozlacznika(r, Q, yL)
  napisy(r, a.etykiety.q, Q, yL + 9, { szer: 32 })
  r.linia(Q + 5, yL, U - 8, yL, dc)
  symbolFalownika(r, U, yL, 16, { nieznany: !a.sprzet.falownik })
  napisy(r, a.etykiety.u, U, yL - 11, { kotwica: 'dol', szer: 40 })
  r.linia(U + 8, yL, RG - 8, yL, ac)
  napisy(r, [a.napiecieAc], U + 10, yL + 2, { wyrownanie: 'lewo', szer: RG - U - 30, kolor: r.kolory.ac })
  symbolSkrzynki(r, RG, yL, 'RG')
  napisy(r, a.etykiety.rg, RG, yL - 9, { kotwica: 'dol', szer: 36 })
  r.linia(RG + 8, yL, P - 7, yL, ac)
  symbolSkrzynki(r, P, yL, 'kWh', { w: 14, licznik: true })
  napisy(r, a.etykiety.p, P, yL - 9, { kotwica: 'dol', szer: 32 })
  r.linia(P + 7, yL, S - 2, yL, ac)
  symbolSieci(r, S, yL)
  napisy(r, a.etykiety.s, S, yL + 10, { szer: 30 })

  // ── Magazyn: pod falownikiem (hybrydowy, strona DC) albo przy linii AC (z własnym falownikiem) ──
  if (a.etykiety.g) {
    // Przy sprzężeniu AC odgałęzienie tuż przed rozdzielnicą, żeby nie przecinało opisu napięcia.
    const gx = a.sprzezenieAc ? RG - 16 : U
    const gy = yL + 32
    if (a.sprzezenieAc) r.linia(gx, yL, gx, gy - 5, ac)
    else r.linia(U, yL + 8, U, gy - 5, dc)
    symbolMagazynu(r, gx, gy)
    napisy(r, [...a.etykiety.g, ...(a.sprzezenieAc ? ['z własnym falownikiem (AC)'] : [])], gx + 10, gy - 2.5, { wyrownanie: 'lewo', szer: 40 })
  }
}

/* ═══ Rozmieszczenie urządzeń ══════════════════════════════════════════════════════════════════════ */

const KIERUNKI = { N: 0, E: 90, S: 180, W: 270 }
const POZA_DOMEM = 18 // mm na papierze dookoła domu (złącze na ogrodzeniu, kierunek północy)
const WYKAZ = 30 // mm pod rysunkiem na wykaz urządzeń
const WNETRZE = ['garaz', 'kotlownia', 'piwnica', 'poddasze', 'inne']

/**
 * Opis arkusza „Rozmieszczenie urządzeń”: obrys domu z rzutu, urządzenia przy ścianach albo w środku, trasy DC
 * i AC, lista do sprawdzenia. Bez obrysu domu (instalacja na gruncie, brak modelu) urządzenia trafiają na listę.
 */
export const opisRozmieszczenia = (dane, { format = FORMAT_DOMYSLNY } = {}) => {
  const uklad = ukladArkusza(format)
  const R = uklad.rysunek
  const m = czescMiejsc(dane) || normalizujMiejsca(null)
  const { sprzet, rzut } = dane
  const dom = rzut?.sciany?.[0] || null
  const zakres = rzut?.zakres || null
  const obszar = { w: R.w - 2 * POZA_DOMEM, h: R.h - 2 * POZA_DOMEM - WYKAZ }
  let k = 0
  let skala = null
  let naPapier = () => [R.x + R.w / 2, R.y + R.h / 2]
  if (zakres) {
    const W = Math.max(zakres.maxX - zakres.minX, 1)
    const H = Math.max(zakres.maxY - zakres.minY, 1)
    const dobor = dobierzSkale((s) => ({ w: W * mmNaMetr(s), h: H * mmNaMetr(s) }), obszar)
    k = dobor.miesci ? mmNaMetr(dobor.skala) : Math.min(obszar.w / W, obszar.h / H)
    skala = dobor.miesci ? dobor.skala : null
    const x0 = R.x + (R.w - W * k) / 2
    const y0 = R.y + POZA_DOMEM + (obszar.h - H * k) / 2
    naPapier = ([X, Y]) => [x0 + (X - zakres.minX) * k, y0 + (Y - zakres.minY) * k]
  }

  // Ściany domu na papierze z kierunkiem na zewnątrz i prawdziwym azymutem tego kierunku.
  const domP = dom ? dom.map(naPapier) : null
  const c = domP ? srodekWielokata(domP) : [R.x + R.w / 2, R.y + R.h / 2]
  const sciany = domP ? domP.map((a, i) => {
    const b = domP[(i + 1) % domP.length]
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    const t = [(b[0] - a[0]) / d, (b[1] - a[1]) / d]
    let n = [-t[1], t[0]]
    const s = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
    if (n[0] * (s[0] - c[0]) + n[1] * (s[1] - c[1]) < 0) n = [-n[0], -n[1]]
    const az = ((Math.atan2(n[0], -n[1]) / RAD - (rzut?.polnoc || 0)) % 360 + 360) % 360
    return { a, b, t, n, d, az }
  }) : []
  const scianaStrony = (strona) => (sciany.length && KIERUNKI[strona] != null
    ? sciany.reduce((best, s) => (Math.abs(((s.az - KIERUNKI[strona] + 540) % 360) - 180) < Math.abs(((best.az - KIERUNKI[strona] + 540) % 360) - 180) ? s : best))
    : null)
  const naScianie = (s, t, odsuniecie) => [s.a[0] + s.t[0] * s.d * t + s.n[0] * odsuniecie, s.a[1] + s.t[1] * s.d * t + s.n[1] * odsuniecie]
  const najblizszaSciana = (p) => sciany.reduce((best, s) => {
    const t = Math.min(1, Math.max(0, ((p[0] - s.a[0]) * s.t[0] + (p[1] - s.a[1]) * s.t[1]) / s.d))
    const q = naScianie(s, t, 0)
    const odl = Math.hypot(p[0] - q[0], p[1] - q[1])
    return !best || odl < best.odl ? { s, t, odl } : best
  }, null)
  // Miejsca umowne w środku domu (urządzenie w znanym pomieszczeniu, ale bez ściany).
  const wolneWSrodku = [[c[0] - 14, c[1] + 6], [c[0] + 14, c[1] + 6], [c[0], c[1] - 8], [c[0], c[1] + 14]]
  const wSrodku = () => wolneWSrodku.shift() || [c[0], c[1]]

  const urzadzenia = []
  const nieznane = []
  const dodaj = (u) => (u.p ? urzadzenia.push(u) : nieznane.push(u))

  // Rozdzielnica: ściana ze strony świata, w środku domu.
  const sRG = znane(m.rozdzielnica.sciana) ? scianaStrony(m.rozdzielnica.sciana) : null
  const pomRG = miejsceRozdzielnicy(m)
  const RG = {
    kod: 'RG', nazwa: 'rozdzielnica główna',
    p: sRG ? naScianie(sRG, 0.3, -6) : domP && pomRG ? wSrodku() : null,
    umowne: !sRG,
    sciana: sRG,
    wiersze: [pomRG, sRG ? `ściana ${({ N: 'północna', E: 'wschodnia', S: 'południowa', W: 'zachodnia' })[m.rozdzielnica.sciana]}` : null].filter(Boolean),
  }
  dodaj(RG)

  // Falownik: położenie z modelu (na ścianie zewnętrznej), w środku przy tej ścianie, gdy audyt mówi o pomieszczeniu.
  const miejsceF = m.falownik.miejsce
  const fModel = dane.falownikNaRzucie && domP ? naPapier(dane.falownikNaRzucie) : null
  const blizej = fModel ? najblizszaSciana(fModel) : null
  let pF = null
  let umowneF = true
  if (fModel && (!znane(miejsceF) || miejsceF === 'sciana')) { pF = naScianie(blizej.s, blizej.t, 6); umowneF = !znane(miejsceF) }
  else if (fModel && WNETRZE.includes(miejsceF)) pF = naScianie(blizej.s, blizej.t, -6)
  else if (domP && miejsceF === 'sciana') pF = naScianie(scianaStrony('N'), 0.6, 6)
  else if (domP && WNETRZE.includes(miejsceF)) pF = wSrodku()
  const wifi = opcja(ZASIEG_WIFI, m.wifi.zasieg)
  const F = {
    kod: 'F', nazwa: 'falownik', p: pF, umowne: umowneF,
    sciana: fModel ? blizej.s : null,
    wiersze: [opcja(OPCJE_FALOWNIKA, miejsceF) && malaLitera(opcja(OPCJE_FALOWNIKA, miejsceF)),
      znane(m.falownik.odleglosc) ? `od RG: ${malaLitera(opcja(ODLEGLOSCI, m.falownik.odleglosc))}` : null,
      wifi ? `Wi-Fi: ${malaLitera(wifi)}` : null].filter(Boolean),
  }
  dodaj(F)

  // Magazyn: obok falownika albo we wskazanym miejscu.
  const zMagazynem = Boolean(sprzet?.magazyn) || znane(m.magazyn.miejsce)
  if (zMagazynem) {
    const mm = m.magazyn.miejsce
    let pM = null
    if (F.p && (mm === 'jak_falownik' || (mm === 'sciana' && miejsceF === 'sciana'))) {
      const s = F.sciana || najblizszaSciana(F.p)?.s
      pM = s ? [F.p[0] + s.t[0] * 12, F.p[1] + s.t[1] * 12] : [F.p[0] + 12, F.p[1]]
    } else if (domP && WNETRZE.includes(mm)) pM = wSrodku()
    dodaj({ kod: 'M', nazwa: 'magazyn energii', p: pM, umowne: mm !== 'jak_falownik', wiersze: [opcja(OPCJE_MAGAZYNU, mm) && malaLitera(opcja(OPCJE_MAGAZYNU, mm))].filter(Boolean) })
  }

  // Złącze z licznikiem: na ścianie rozdzielnicy (albo południowej), na ogrodzeniu dalej od domu, w środku przy RG.
  const gdzieZK = m.licznik.gdzie
  const sZK = sRG || scianaStrony('S')
  let pZK = null
  if (domP && gdzieZK === 'elewacja') pZK = naScianie(sZK, 0.3, 6)
  else if (domP && gdzieZK === 'ogrodzenie') pZK = naScianie(sZK, 0.3, 14)
  else if (domP && gdzieZK === 'wewnatrz') pZK = RG.p && !RG.umowne ? naScianie(sRG, 0.55, -6) : wSrodku()
  dodaj({
    kod: 'ZK', nazwa: 'złącze z licznikiem', p: pZK, umowne: gdzieZK === 'ogrodzenie' || !sRG,
    wiersze: [miejsceLicznika(m)].filter(Boolean),
  })

  // Trasy: DC od najbliższego modułu do falownika, AC falownik, rozdzielnica, złącze.
  const moduly = (rzut?.polacie || []).flatMap((p) => p.moduly)
  let dcOd = null
  if (F.p && moduly.length) {
    dcOd = moduly.map((x) => srodekWielokata(x.plan.map(naPapier))).reduce((best, q) => (!best || Math.hypot(q[0] - F.p[0], q[1] - F.p[1]) < Math.hypot(best[0] - F.p[0], best[1] - F.p[1]) ? q : best), null)
  }
  const droga = opcja(DROGI_KABLI, m.kable.droga)
  const ZK = urzadzenia.find((u) => u.kod === 'ZK')
  const trasy = [
    dcOd && { rodzaj: 'dc', punkty: [dcOd, F.p], przerywana: !droga, napis: droga ? `kable DC: ${malaLitera(droga)}` : 'droga kabli DC do sprawdzenia' },
    F.p && RG.p && { rodzaj: 'ac', punkty: [F.p, RG.p], przerywana: F.umowne || RG.umowne, napis: null },
    RG.p && ZK && { rodzaj: 'ac', punkty: [RG.p, ZK.p], przerywana: RG.umowne || ZK.umowne, napis: null },
  ].filter(Boolean)

  const kontekst = {
    zMagazynem, mocModulowKw: ((sprzet?.moduly?.liczba || 0) * (sprzet?.moduly?.mocW || 0)) / 1000 || null,
    mocFalownikaKw: sprzet?.falownik?.mocKw || null, mocMagazynuKw: null,
  }
  const lista = doSprawdzenia(m, kontekst).map((x) => x.tekst.replace(/\bfirma\b/g, 'wykonawca'))

  return {
    rodzaj: 'rozmieszczenie',
    tytul: 'Rozmieszczenie urządzeń',
    uklad,
    skala,
    k,
    naPapier,
    domP,
    scianyP: (rzut?.sciany || []).map((w) => w.map(naPapier)),
    dachP: (rzut?.polacie || []).filter((p) => !p.grunt).flatMap((p) => (p.krawedzieZGory || p.krawedzie || []).map((e) => e.plan.map(naPapier))),
    modulyP: moduly.map((x) => x.plan.map(naPapier)),
    urzadzenia,
    nieznane,
    trasy,
    polnoc: rzut ? { x: R.x + 6, y: R.y + 6, kat: rzut.polnoc } : null,
    wykaz: { x: R.x, y: R.y + R.h - WYKAZ, w: R.w },
    legenda: [
      { rysuj: (r, cx, cy) => symbolUrzadzenia(r, cx, cy, 'F'), opis: 'Falownik' },
      ...(zMagazynem ? [{ rysuj: (r, cx, cy) => symbolUrzadzenia(r, cx, cy, 'M'), opis: 'Magazyn energii' }] : []),
      { rysuj: (r, cx, cy) => symbolUrzadzenia(r, cx, cy, 'RG'), opis: 'Rozdzielnica główna budynku' },
      { rysuj: (r, cx, cy) => symbolUrzadzenia(r, cx, cy, 'ZK'), opis: 'Złącze z licznikiem energii' },
      { rysuj: symbolPrzewodu('dc'), opis: 'Przewody DC z dachu' },
      { rysuj: symbolPrzewodu('ac'), opis: 'Przewód AC' },
      { rysuj: (r, cx, cy) => symbolUrzadzenia(r, cx, cy, '?', { umowne: true }), opis: 'Położenie umowne albo do sprawdzenia' },
    ],
    opis: { naglowek: 'Do sprawdzenia na miejscu', wiersze: lista },
    rysujTresc(r) { rysujRozmieszczenie(r, this) },
  }
}

const rysujRozmieszczenie = (r, a) => {
  const R = a.uklad.rysunek
  // Dach i moduły jasno w tle, ściany domu grubo.
  for (const [p, q] of a.dachP) r.linia(p[0], p[1], q[0], q[1], { grubosc: 0.13, kolor: r.kolory.szary })
  for (const w of a.modulyP) r.wielokat(w, { grubosc: 0.08, kolor: r.kolory.jasny })
  for (const w of a.scianyP) r.wielokat(w, { grubosc: 0.6 })
  if (a.polnoc) rysujPolnoc(r, a.polnoc.x + 4, a.polnoc.y + 4, a.polnoc.kat)

  for (const t of a.trasy) {
    const [p, q] = t.punkty
    r.linia(p[0], p[1], q[0], q[1], { grubosc: t.rodzaj === 'dc' ? 0.5 : 0.4, kolor: r.kolory[t.rodzaj], kreski: t.przerywana ? KRESKI : null })
    if (t.napis) {
      const [sx, sy] = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
      const w = r.szer(t.napis, MALY)
      r.prostokat(sx + 1.5, sy - 2.4, w + 1.2, 3.2, { grubosc: 0, wypelnienie: r.kolory.bialy })
      r.tekst(t.napis, sx + 2.1, sy, { rozmiar: MALY, kolor: r.kolory.dc })
    }
  }

  for (const u of a.urzadzenia) symbolUrzadzenia(r, u.p[0], u.p[1], u.kod, { umowne: u.umowne })

  // Wykaz pod rysunkiem: każde urządzenie z opisem miejsca; bez położenia na planie: „do sprawdzenia na miejscu”.
  // Opisy w wykazie, nie przy symbolach: kilka urządzeń przy jednej ścianie nie zasłania sobie podpisów.
  const { x, y, w } = a.wykaz
  const wszystkie = [...a.urzadzenia, ...a.nieznane.map((u) => ({ ...u, brak: true }))]
  const kolumna = (w - 4) / 2
  wszystkie.forEach((u, i) => {
    const cx = x + 7 + (i % 2) * kolumna
    const cy = y + 6 + Math.floor(i / 2) * 11
    symbolUrzadzenia(r, cx, cy, u.kod, { umowne: u.umowne || u.brak })
    const opis = [u.wiersze.length ? u.wiersze.join(', ') : null,
      u.brak ? 'położenie do sprawdzenia na miejscu' : u.umowne ? 'położenie na rysunku umowne' : null].filter(Boolean).join('; ')
    napisy(r, [`${u.nazwa.charAt(0).toUpperCase()}${u.nazwa.slice(1)}${opis ? `: ${opis}` : ''}`], cx + 6.5, cy - 4, { wyrownanie: 'lewo', szer: kolumna - 16 })
  })
  const dopisek = 'Położenie urządzeń i tras orientacyjne, z odpowiedzi klienta. Wykonawca ustala je na miejscu.'
  r.tekst(dopisek, R.x + 2, R.y + R.h - 1, { rozmiar: ROZMIAR.maly, kolor: r.kolory.szary })
}

/* ═══ Całość ═══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Arkusze instalacji: schemat i rozmieszczenie (to drugie tylko z obrysem domu albo z audytem).
 * Do łączenia z rzutem dachu w teczce: `ponumerujArkusze([...rzut.arkusze, ...instalacja.arkusze])`.
 */
export const opisRysunkowInstalacji = (dane, { format = FORMAT_DOMYSLNY, ...opcje } = {}) => {
  const arkusze = [opisSchematuInstalacji(dane, { format })]
  if (dane.rzut?.sciany?.length || dane.miejsca) arkusze.push(opisRozmieszczenia(dane, { format }))
  return { tytul: 'Schemat i rozmieszczenie instalacji', format, arkusze: ponumerujArkusze(arkusze, opcje) }
}

export const narysujRysunkiInstalacji = (opis, biblioteki, czcionka) => narysujArkusze(opis, biblioteki, czcionka)

/** W przeglądarce: pdf-lib i fontkit ładowane dopiero przy generowaniu. */
export const rysujRysunkiInstalacji = (dane, opcje, urlCzcionki) => rysujArkusze(opisRysunkowInstalacji(dane, opcje), urlCzcionki)
